import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtNZ } from "@/lib/datetime";
import { toast } from "sonner";
import { Download, Activity as ActivityIcon, LogIn, LogOut, User, Briefcase, MapPin, X, Mail } from "lucide-react";

const ACTION_META = {
  login: { label: "Login", icon: LogIn, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  logout: { label: "Logout", icon: LogOut, cls: "bg-zinc-100 text-zinc-600 border-zinc-300" },
  "user.create": { label: "User Created", icon: User, cls: "bg-blue-100 text-blue-800 border-blue-300" },
  "user.update": { label: "User Updated", icon: User, cls: "bg-blue-50 text-brand border-blue-200" },
  "user.delete": { label: "User Deleted", icon: User, cls: "bg-red-100 text-red-700 border-red-300" },
  "user.bulk_import": { label: "Users Imported", icon: User, cls: "bg-blue-100 text-blue-800 border-blue-300" },
  "job.create": { label: "Job Created", icon: Briefcase, cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  "job.update": { label: "Job Updated", icon: Briefcase, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  "job.delete": { label: "Job Deleted", icon: Briefcase, cls: "bg-red-100 text-red-700 border-red-300" },
  "job.status": { label: "Status Change", icon: Briefcase, cls: "bg-amber-100 text-amber-800 border-amber-300" },
  "job.bulk_import": { label: "Jobs Imported", icon: Briefcase, cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  "location.create": { label: "Location Added", icon: MapPin, cls: "bg-purple-100 text-purple-800 border-purple-300" },
  "location.rename": { label: "Location Renamed", icon: MapPin, cls: "bg-purple-50 text-purple-700 border-purple-200" },
  "location.delete": { label: "Location Deleted", icon: MapPin, cls: "bg-red-100 text-red-700 border-red-300" },
  "digest.sent": { label: "Weekly Digest Sent", icon: Mail, cls: "bg-teal-100 text-teal-800 border-teal-300" },
};

function actionMeta(a) {
  return ACTION_META[a] || { label: a, icon: ActivityIcon, cls: "bg-zinc-100 text-zinc-700 border-zinc-300" };
}

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "all", job_id: "all", start: "", end: "" });
  const [jobQuery, setJobQuery] = useState("");
  const [jobOpen, setJobOpen] = useState(false);
  const jobRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (jobRef.current && !jobRef.current.contains(e.target)) setJobOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filteredJobs = jobQuery.trim()
    ? jobs.filter((j) => (j.title || "").toLowerCase().includes(jobQuery.trim().toLowerCase()))
    : jobs;

  const selectJob = (id, title) => {
    const next = { ...filters, job_id: id };
    setFilters(next);
    setJobQuery(id === "all" ? "" : title);
    setJobOpen(false);
    load(next);
  };

  const buildQuery = (f = filters) => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
      if (v && v !== "all") p.append(k, v);
    });
    return p.toString();
  };

  const load = (f = filters) => {
    setLoading(true);
    api
      .get(`/activity?${buildQuery(f)}`)
      .then((r) => {
        setLogs(r.data.logs);
        setActions(r.data.actions);
        setJobs(r.data.jobs || []);
      })
      .catch((err) => toast.error(apiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/activity/export?${buildQuery()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "activity_log.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Activity log exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Audit</div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">
            Activity Log
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">Every key action, with who did it and when.</p>
        </div>
        <button
          onClick={exportCsv}
          data-testid="export-activity-button"
          className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2"
        >
          <Download size={18} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-4 mb-6 grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">Action</label>
          <select
            value={filters.action}
            onChange={(e) => {
              const next = { ...filters, action: e.target.value };
              setFilters(next);
              load(next);
            }}
            data-testid="activity-action-filter"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {actionMeta(a).label}
              </option>
            ))}
          </select>
        </div>
        <div ref={jobRef} className="relative">
          <label className="text-xs font-bold text-zinc-600 block mb-1">Job</label>
          <div className="relative">
            <input
              type="text"
              value={jobQuery}
              onChange={(e) => {
                setJobQuery(e.target.value);
                setJobOpen(true);
                if (e.target.value === "" && filters.job_id !== "all") {
                  const next = { ...filters, job_id: "all" };
                  setFilters(next);
                  load(next);
                }
              }}
              onFocus={() => setJobOpen(true)}
              placeholder="Search jobs…"
              data-testid="activity-job-search"
              autoComplete="off"
              className="w-full px-3 py-2 pr-8 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
            />
            {(jobQuery || filters.job_id !== "all") && (
              <button
                type="button"
                onClick={() => selectJob("all", "")}
                data-testid="activity-job-clear"
                aria-label="Clear job filter"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 p-0.5"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {jobOpen && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 shadow-lg max-h-60 overflow-auto" data-testid="activity-job-options">
              <button
                type="button"
                onClick={() => selectJob("all", "")}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 ${filters.job_id === "all" ? "font-bold text-brand" : "text-zinc-700"}`}
              >
                All jobs
              </button>
              {filteredJobs.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => selectJob(j.id, j.title)}
                  data-testid={`activity-job-option-${j.id}`}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 truncate ${filters.job_id === j.id ? "font-bold text-brand" : "text-zinc-700"}`}
                >
                  {j.title}
                </button>
              ))}
              {filteredJobs.length === 0 && (
                <div className="px-3 py-2 text-sm text-zinc-400">No matching jobs</div>
              )}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">From</label>
          <input
            type="date"
            value={filters.start}
            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
            data-testid="activity-start-date"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">To</label>
          <input
            type="date"
            value={filters.end}
            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
            data-testid="activity-end-date"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={() => load()}
            data-testid="apply-activity-filters"
            className="w-full bg-zinc-950 text-white rounded-full font-bold tracking-tight hover:bg-zinc-800 transition-colors px-6 py-2 text-sm"
          >
            Apply
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Timestamp</th>
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">User</th>
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Action</th>
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Job</th>
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-400 font-mono text-sm">Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-400 text-sm">No activity recorded.</td></tr>
            ) : (
              logs.map((l) => {
                const m = actionMeta(l.action);
                const Icon = m.icon;
                return (
                  <tr key={l.id} data-testid={`activity-row-${l.id}`} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3 text-zinc-600 tabular-nums whitespace-nowrap">
                      {fmtNZ(l.at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-zinc-900">{l.user_name || "—"}</div>
                      <div className="text-xs text-zinc-500">{l.user_email} · {l.role}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${m.cls}`}>
                        <Icon size={12} /> {m.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {l.job_title ? (
                        l.job_id ? (
                          <Link to={`/jobs/${l.job_id}`} data-testid={`activity-job-link-${l.id}`} className="inline-flex items-center gap-1 font-semibold text-brand hover:underline">
                            <Briefcase size={12} /> {l.job_title}
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-zinc-600">
                            <Briefcase size={12} /> {l.job_title}
                          </span>
                        )
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{l.detail}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
